import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, X, Save, Trash2, ChevronLeft, ChevronRight, Filter as FilterIcon, Check, MessageSquarePlus, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STOCK_COLORS = {
  out: 'bg-red-500/15 text-red-400 border-red-500/30',
  low: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  ok: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  na: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

function StockBadge({ inStock, minStock }) {
  if (minStock <= 0) return <span className={`text-xs px-2 py-0.5 rounded-full border ${STOCK_COLORS.na}`}>{inStock}</span>;
  if (inStock === 0) return <span className={`text-xs px-2 py-0.5 rounded-full border ${STOCK_COLORS.out}`}>OUT</span>;
  if (inStock <= minStock) return <span className={`text-xs px-2 py-0.5 rounded-full border ${STOCK_COLORS.low}`}>{inStock} Low</span>;
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${STOCK_COLORS.ok}`}>{inStock}</span>;
}

export default function InventoryPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStock, setFilterStock] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [unitFilters, setUnitFilters] = useState([]);
  const [showUnitFilters, setShowUnitFilters] = useState(false);
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [activityPart, setActivityPart] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityNote, setActivityNote] = useState('');
  const [activityQty, setActivityQty] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const fetchParts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterCategory) params.set('category', filterCategory);
      if (filterStock) params.set('stock_status', filterStock);
      if (filterLocation) params.set('location', filterLocation);
      params.set('page', page);
      params.set('per_page', 100);
      const r = await fetch(`${API}/api/inventory?${params}`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) {
        setParts(d.parts);
        setTotal(d.total);
        setTotalPages(d.pages);
        setStats(d.stats || {});
        if (d.category_counts) {
          setCategories(Object.entries(d.category_counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));
        }
      }
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [search, filterCategory, filterStock, filterLocation, page, token]);

  const fetchUnitFilters = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/inventory/unit-filters`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setUnitFilters(d.unit_filters);
    } catch {}
  }, [token]);

  const fetchActivity = async (partId) => {
    setActivityLoading(true);
    try {
      const r = await fetch(`${API}/api/inventory/${partId}/activity`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setActivity(d.activity);
    } catch {}
    setActivityLoading(false);
  };

  useEffect(() => { fetchParts(); }, [fetchParts]);
  useEffect(() => { fetchUnitFilters(); }, [fetchUnitFilters]);

  const emptyForm = { part_number: '', part_name: '', category: '', description: '', price: '', in_stock: 0, max_stock: 0, min_stock: 0, storage_location: '', notes: '' };
  const startCreate = () => { setCreating(true); setEditing(null); setForm(emptyForm); setActivityPart(null); };
  const startEdit = (p) => { setEditing(p.id); setCreating(false); setForm({ ...p, price: p.price ?? '' }); };
  const cancel = () => { setEditing(null); setCreating(false); };

  const openActivity = (p) => {
    setActivityPart(p);
    setActivityNote('');
    setActivityQty('');
    fetchActivity(p.id);
  };

  const handleSave = async () => {
    if (!form.part_number?.trim()) { toast.error('Part number is required'); return; }
    try {
      const body = { ...form, price: form.price === '' ? null : Number(form.price), in_stock: Number(form.in_stock) || 0, max_stock: Number(form.max_stock) || 0, min_stock: Number(form.min_stock) || 0 };
      const url = editing ? `${API}/api/inventory/${editing}` : `${API}/api/inventory`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!r.ok) { toast.error('Save failed'); return; }
      toast.success(editing ? 'Part updated' : 'Part added');
      cancel(); fetchParts();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id, pn) => {
    if (!window.confirm(`Delete part ${pn}?`)) return;
    try { await fetch(`${API}/api/inventory/${id}`, { method: 'DELETE', headers }); toast.success('Part deleted'); fetchParts(); }
    catch { toast.error('Delete failed'); }
  };

  const handleAddActivity = async () => {
    if (!activityNote.trim() && !activityQty) { toast.error('Enter a note or quantity change'); return; }
    try {
      const body = { note: activityNote, qty_change: activityQty ? parseInt(activityQty) : null, author: '', activity_date: activityDate };
      const r = await fetch(`${API}/api/inventory/${activityPart.id}/activity`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!r.ok) { toast.error('Failed to add note'); return; }
      toast.success('Activity logged');
      setActivityNote('');
      setActivityQty('');
      fetchActivity(activityPart.id);
      fetchParts(); // refresh stock counts
    } catch { toast.error('Failed'); }
  };

  const EditRow = ({ p }) => (
    <tr className="border-b bg-muted/20">
      <td colSpan={10} className="p-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-2">
          <div><label className="text-xs text-muted-foreground block">Part # *</label><Input className="h-8 text-xs" value={form.part_number || ''} onChange={e => setForm(f => ({ ...f, part_number: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Supplier</label><Input className="h-8 text-xs" value={form.part_name || ''} onChange={e => setForm(f => ({ ...f, part_name: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Category</label>
            <select value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs">
              <option value="">Select...</option>
              {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground block">Description</label><Input className="h-8 text-xs" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Price</label><Input className="h-8 text-xs" type="number" step="0.01" value={form.price ?? ''} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">In Stock</label><Input className="h-8 text-xs" type="number" value={form.in_stock || 0} onChange={e => setForm(f => ({ ...f, in_stock: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Max</label><Input className="h-8 text-xs" type="number" value={form.max_stock || 0} onChange={e => setForm(f => ({ ...f, max_stock: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Min</label><Input className="h-8 text-xs" type="number" value={form.min_stock || 0} onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Location</label><Input className="h-8 text-xs" value={form.storage_location || ''} onChange={e => setForm(f => ({ ...f, storage_location: e.target.value }))} /></div>
          <div><label className="text-xs text-muted-foreground block">Notes</label><Input className="h-8 text-xs" value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="save-part-btn" onClick={handleSave} size="sm" className="h-7 text-xs"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancel}>Cancel</Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div data-testid="inventory-page" className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Total Parts</div>
          <div className="text-xl font-bold" data-testid="stat-total">{stats.total_parts?.toLocaleString() || 0}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Inventory Value</div>
          <div className="text-xl font-bold text-emerald-400" data-testid="stat-value">${(stats.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Low Stock</div>
          <div className="text-xl font-bold text-amber-400" data-testid="stat-low">{stats.low_stock || 0}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Out of Stock</div>
          <div className="text-xl font-bold text-red-400" data-testid="stat-out">{stats.out_of_stock || 0}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="inventory-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search part #, name, description..." className="pl-9" />
        </div>
        <select data-testid="filter-category" value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
        </select>
        <select data-testid="filter-stock" value={filterStock} onChange={e => { setFilterStock(e.target.value); setPage(1); }} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All Stock</option>
          <option value="low">Low Stock</option>
          <option value="out">Out of Stock</option>
        </select>
        <Input data-testid="filter-location" value={filterLocation} onChange={e => { setFilterLocation(e.target.value); setPage(1); }} placeholder="Location..." className="w-28 h-9" />
        {(filterCategory || filterStock || filterLocation) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterCategory(''); setFilterStock(''); setFilterLocation(''); setPage(1); }}>Clear</Button>
        )}
        <Button data-testid="toggle-unit-filters" variant={showUnitFilters ? "default" : "outline"} size="sm" onClick={() => setShowUnitFilters(!showUnitFilters)}>
          <FilterIcon className="h-4 w-4 mr-1" />Unit Filters
        </Button>
        <Button data-testid="add-part-btn" onClick={startCreate} size="sm"><Plus className="h-4 w-4 mr-1" />Add Part</Button>
      </div>

      {/* Unit Filters Panel */}
      {showUnitFilters && (
        <div data-testid="unit-filters-panel" className="bg-card border rounded-xl p-4 space-y-2">
          <h3 className="font-semibold text-sm">Unit Filter Kits</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {unitFilters.map(uf => (
              <button key={uf.unit_name} onClick={() => setExpandedUnit(expandedUnit === uf.unit_name ? null : uf.unit_name)}
                className={`text-left p-2 rounded-lg border text-xs transition-colors ${expandedUnit === uf.unit_name ? 'bg-primary/10 border-primary/40' : 'bg-muted/20 hover:bg-muted/40'}`}>
                <div className="font-mono font-bold">{uf.unit_name}</div>
                <div className="text-muted-foreground">{uf.filters?.length || 0} filters</div>
              </button>
            ))}
          </div>
          {expandedUnit && unitFilters.find(u => u.unit_name === expandedUnit) && (
            <div className="mt-3 border rounded-lg p-3 bg-muted/10">
              <div className="font-semibold text-sm mb-2">Filters for {expandedUnit}</div>
              <table className="w-full text-xs">
                <thead><tr className="border-b"><th className="text-left p-1.5">Part #</th><th className="text-left p-1.5">Type</th><th className="text-left p-1.5">Qty</th></tr></thead>
                <tbody>
                  {unitFilters.find(u => u.unit_name === expandedUnit).filters.map((f, i) => (
                    <tr key={i} className="border-b last:border-0"><td className="p-1.5 font-mono">{f.part_number}</td><td className="p-1.5">{f.filter_type}</td><td className="p-1.5">{f.qty}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Form (top - only for new) */}
      {creating && (
        <div data-testid="part-form" className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">New Part</h2>
            <Button variant="ghost" size="icon" onClick={cancel}><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Part # *</label><Input data-testid="input-part-number" value={form.part_number || ''} onChange={e => setForm(p => ({ ...p, part_number: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Supplier</label><Input value={form.part_name || ''} onChange={e => setForm(p => ({ ...p, part_name: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select value={form.category || ''} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Select...</option>{categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Description</label><Input value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Price</label><Input type="number" step="0.01" value={form.price ?? ''} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">In Stock</label><Input type="number" value={form.in_stock || 0} onChange={e => setForm(p => ({ ...p, in_stock: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Max</label><Input type="number" value={form.max_stock || 0} onChange={e => setForm(p => ({ ...p, max_stock: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Min</label><Input type="number" value={form.min_stock || 0} onChange={e => setForm(p => ({ ...p, min_stock: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Location</label><Input value={form.storage_location || ''} onChange={e => setForm(p => ({ ...p, storage_location: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Notes</label><Input value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-part-btn" onClick={handleSave} size="sm"><Save className="h-4 w-4 mr-1" />Save</Button>
            <Button variant="outline" size="sm" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Activity Log Side Panel */}
      {activityPart && (
        <div data-testid="activity-panel" className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-sm">Activity: {activityPart.part_number}</h2>
              <p className="text-xs text-muted-foreground">{activityPart.description} — Current stock: {activityPart.in_stock}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setActivityPart(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-2 items-end">
            <div className="w-36">
              <label className="text-xs text-muted-foreground block mb-1">Date</label>
              <Input className="h-8 text-xs" type="date" value={activityDate} onChange={e => setActivityDate(e.target.value)} />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Note</label>
              <Input className="h-8 text-xs" placeholder="e.g. Received shipment, used for WO #123..." value={activityNote} onChange={e => setActivityNote(e.target.value)} />
            </div>
            <div className="w-24">
              <label className="text-xs text-muted-foreground block mb-1">Qty +/-</label>
              <Input className="h-8 text-xs" type="number" placeholder="+5 or -2" value={activityQty} onChange={e => setActivityQty(e.target.value)} />
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={handleAddActivity}><MessageSquarePlus className="h-3.5 w-3.5 mr-1" />Log</Button>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {activityLoading ? <div className="text-xs text-muted-foreground py-2">Loading...</div> :
              activity.length === 0 ? <div className="text-xs text-muted-foreground py-2">No activity yet</div> :
              activity.map(a => (
                <div key={a.id} className="flex gap-2 text-xs border-b border-border/50 py-1.5">
                  <span className="text-muted-foreground whitespace-nowrap">{a.activity_date || new Date(a.created_at).toLocaleDateString()}</span>
                  {a.qty_change != null && (
                    <span className={`font-mono font-bold whitespace-nowrap ${a.qty_change > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.qty_change > 0 ? '+' : ''}{a.qty_change} ({a.stock_before} &#8594; {a.stock_after})
                    </span>
                  )}
                  <span className="flex-1">{a.note}</span>
                  {a.author && <span className="text-muted-foreground">— {a.author}</span>}
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <div className="text-center py-8 text-muted-foreground">Loading inventory...</div> : (
        <>
          <div className="border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-left p-3 font-medium">Part #</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">Supplier</th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">Category</th>
                    <th className="text-right p-3 font-medium">Price</th>
                    <th className="text-center p-3 font-medium">Stock</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">Location</th>
                    <th className="text-center p-3 font-medium" title="In Fleetio">F</th>
                    <th className="text-center p-3 font-medium" title="In Google Sheet">GS</th>
                    <th className="p-3 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p, i) => editing === p.id ? (
                    <EditRow key={p.id} p={p} />
                  ) : (
                    <tr key={p.id || i} data-testid={`part-row-${i}`} className="border-b last:border-0 hover:bg-muted/10 transition-colors">
                      <td className="p-3 font-mono text-xs font-bold max-w-[180px] truncate" title={p.part_number}>{p.part_number}</td>
                      <td className="p-3 text-xs text-muted-foreground hidden sm:table-cell max-w-[120px] truncate" title={p.part_name}>{p.part_name}</td>
                      <td className="p-3 text-xs max-w-[200px] truncate" title={p.description}>{p.description}</td>
                      <td className="p-3 hidden md:table-cell">
                        {p.category && <span className="text-xs px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">{p.category}</span>}
                      </td>
                      <td className="p-3 text-right font-mono text-xs">{p.price != null ? `$${p.price.toFixed(2)}` : '—'}</td>
                      <td className="p-3 text-center"><StockBadge inStock={p.in_stock} minStock={p.min_stock} /></td>
                      <td className="p-3 text-xs text-muted-foreground font-mono hidden lg:table-cell">{p.storage_location}</td>
                      <td className="p-3 text-center">{p.in_fleetio && <Check className="h-4 w-4 text-blue-400 inline-block" />}</td>
                      <td className="p-3 text-center">{p.in_google_sheet && <Check className="h-4 w-4 text-green-400 inline-block" />}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openActivity(p)} data-testid={`activity-part-${i}`} title="Activity log"><MessageSquarePlus className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p)} data-testid={`edit-part-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDelete(p.id, p.part_number)} data-testid={`delete-part-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {parts.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">No parts found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Showing {((page - 1) * 100) + 1}–{Math.min(page * 100, total)} of {total}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="px-3 py-1">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
